import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { VideoCall } from '@mui/icons-material';
import edgeFunctionService from '../../services/edgeFunctionService';

/** Plans that include the meeting notetaker bot. */
const NOTETAKER_TIERS = ['pro-plus', 'enterprise'];

/**
 * Whether the meeting notetaker (Recall.ai bot) is available for this user:
 * the plan must include it and the deployment must have Recall configured.
 *
 * @param {object} params
 * @param {string|undefined} params.userId - Signed-in user id.
 * @param {string} params.userTier - Plan tier from usageService.getUserTier.
 * @returns {boolean|null} null while unknown.
 */
export function useNotetakerAvailability({ userId, userTier }) {
  const tierEnabled = NOTETAKER_TIERS.includes(userTier);
  const [available, setAvailable] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;
    if (!tierEnabled) {
      setAvailable(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await edgeFunctionService.callFunction('recall-notetaker', { action: 'ping' });
        if (!cancelled) setAvailable(Boolean(result?.available));
      } catch (err) {
        console.warn('Error checking Recall availability:', err);
        if (!cancelled) setAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, tierEnabled]);

  return available;
}

/**
 * Send the notetaker bot to a Teams, Google Meet or Zoom meeting. When the
 * meeting ends its transcript appears in Unlinked Sessions on Home.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.onClose - Close handler.
 * @param {string|undefined} props.userId - User id.
 * @param {string|undefined} props.accountId - Account id.
 * @returns {JSX.Element}
 */
function NotetakerDialog({ open, onClose, userId, accountId }) {
  const [meetingUrl, setMeetingUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setSuccess('');
    }
  }, [open]);

  const handleSend = useCallback(async () => {
    if (!meetingUrl.trim()) {
      setError('Enter a meeting URL (Teams, Google Meet, or Zoom).');
      return;
    }
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const result = await edgeFunctionService.callFunction('recall-notetaker', {
        action: 'create-bot',
        userId,
        accountId,
        meetingUrl: meetingUrl.trim(),
      });
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to send bot to meeting.');
      }
      setSuccess('The notetaker is joining. When the meeting ends, its transcript appears under Unlinked Sessions on Home.');
      setMeetingUrl('');
    } catch (err) {
      console.error('Error sending notetaker bot:', err);
      setError(err.message || 'Failed to send bot. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [meetingUrl, userId, accountId]);

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth disableRestoreFocus>
      <DialogTitle>Record a live meeting</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste a Microsoft Teams, Google Meet or Zoom join link. A Quill AI notetaker joins, records the meeting
          and creates a session on Home when it ends.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {!success && (
          <TextField
            fullWidth
            label="Meeting URL"
            placeholder="https://teams.microsoft.com/l/meetup-join/..."
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && meetingUrl.trim() && !submitting) handleSend();
            }}
            disabled={submitting}
            autoFocus
            helperText="Use the join link from the calendar invite or the meeting app."
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={submitting || !meetingUrl.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <VideoCall />}
          >
            {submitting ? 'Sending...' : 'Send notetaker'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default NotetakerDialog;
